import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ShieldCheck, Clock, Pill, ArrowRight, ChevronRight,
  Activity, User, CalendarClock, CheckCircle2, XCircle, Timer,
} from "lucide-react";
import PersonAvatar from "@/components/PersonAvatar";
import { Button } from "@/components/ui/button";

// ---- Formatting -----------------------------------------------------------
function formatOverdue(minutes) {
  const m = Math.max(0, Math.floor(minutes));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${String(rm).padStart(2, "0")}m`;
}
function formatCountdown(minutes) {
  const m = Math.max(0, Math.floor(minutes));
  if (m < 1) return "any minute now";
  if (m < 60) return `Due in ${m} minute${m === 1 ? "" : "s"}`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `Due in ${h}h ${rm}m` : `Due in ${h}h`;
}
function priorityFor(minutes) {
  if (minutes >= 120) return { key: "critical", label: "CRITICAL", chip: "bg-red-900 text-white border-red-900" };
  if (minutes >= 30)  return { key: "urgent",   label: "URGENT",   chip: "bg-orange-500 text-white border-orange-600" };
  return                     { key: "warning",  label: "DUE SOON", chip: "bg-amber-400 text-amber-950 border-amber-500" };
}
function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}
function scheduleLabel(scheduledAtIso, scheduledTime) {
  if (!scheduledAtIso) return `• ${scheduledTime}`;
  const s = new Date(scheduledAtIso);
  const now = new Date();
  const y = new Date(); y.setDate(y.getDate() - 1);
  const t = new Date(); t.setDate(t.getDate() + 1);
  const day = isSameDay(s, now) ? "Today" : isSameDay(s, y) ? "Yesterday" : isSameDay(s, t) ? "Tomorrow"
    : s.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" });
  return `Due ${day} • ${scheduledTime}`;
}
function relativeTime(fromDate) {
  const s = Math.max(0, Math.floor((Date.now() - fromDate.getTime()) / 1000));
  if (s < 15) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}

// ---- Shared bits ----------------------------------------------------------
function TopAccent({ tone = "red" }) {
  const bg =
    tone === "red"    ? "linear-gradient(90deg, #DC2626 0%, #B91C1C 50%, #DC2626 100%)"
    : tone === "amber" ? "linear-gradient(90deg, #D97706 0%, #B45309 50%, #D97706 100%)"
    : "linear-gradient(90deg, #059669 0%, #047857 50%, #059669 100%)";
  return (
    <div
      aria-hidden="true"
      style={{ height: 4, background: bg, boxShadow: "0 1px 0 rgba(0,0,0,0.04)" }}
      data-testid="med-safety-accent"
    />
  );
}

function LastUpdated({ at, testid = "med-safety-updated" }) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  const label = relativeTime(at);
  const abs = at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={testid} title={`Last refresh: ${abs}`}>
      <Timer className="w-3 h-3" aria-hidden="true" />
      <span>Updated {label}</span>
      <span className="text-slate-300">·</span>
      <span className="tabular-nums text-slate-500">{abs}</span>
    </div>
  );
}

// ---- Today's Activity panel (right column when no upcoming) ---------------
function TodayActivityPanel({ summary }) {
  const rows = [
    { key: "scheduled",    label: "Scheduled",    val: summary?.scheduled ?? 0,    tone: "slate",   Icon: CalendarClock },
    { key: "administered", label: "Administered", val: summary?.administered ?? 0, tone: "emerald", Icon: CheckCircle2 },
    { key: "due_soon",     label: "Due Soon",     val: summary?.due_soon ?? 0,     tone: "amber",   Icon: Clock },
    { key: "overdue",      label: "Overdue",      val: summary?.overdue ?? 0,      tone: "red",     Icon: AlertTriangle },
    { key: "missed",       label: "Missed",       val: summary?.missed ?? 0,       tone: "rose",    Icon: XCircle },
  ];
  const toneClasses = {
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-900 border-amber-200",
    red:     "bg-red-50 text-red-800 border-red-200",
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
  };
  const iconTone = {
    slate:   "text-slate-500",
    emerald: "text-emerald-600",
    amber:   "text-amber-600",
    red:     "text-red-600",
    rose:    "text-rose-600",
  };
  return (
    <div data-testid="med-safety-today-panel">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">
        <Activity className="w-3 h-3" aria-hidden="true" />
        Today&apos;s Medication Activity
      </div>
      <ul className="mt-2 space-y-1" data-testid="med-safety-today-list">
        {rows.map(({ key, label, val, tone, Icon }) => (
          <li
            key={key}
            className={`flex items-center justify-between px-2 py-1 rounded-md border ${toneClasses[tone]}`}
            data-testid={`med-safety-today-${key}`}
          >
            <span className="flex items-center gap-1.5 text-[12px] font-medium">
              <Icon className={`w-3.5 h-3.5 ${iconTone[tone]}`} aria-hidden="true" />
              {label}
            </span>
            <span className="text-sm font-heading font-semibold tabular-nums">{val}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---- Next Dose panel (right column when meds are upcoming) ----------------
function NextDosePanel({ nextDose }) {
  return (
    <div data-testid="med-safety-next-panel">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">
        <Clock className="w-3 h-3" aria-hidden="true" />
        Next Medication Due
      </div>
      <div className="mt-2 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-sky-100 border border-sky-300 flex items-center justify-center shrink-0" aria-hidden="true">
          <User className="w-4 h-4 text-sky-700" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate leading-tight flex items-center gap-1" data-testid="med-safety-next-client">
            {nextDose.client_name}
          </div>
          <div className="text-[13px] text-slate-600 truncate leading-snug flex items-center gap-1" data-testid="med-safety-next-med">
            <Pill className="w-3.5 h-3.5 text-sky-700 shrink-0" aria-hidden="true" />
            {nextDose.medication_name}
          </div>
        </div>
      </div>
      <div className="mt-2 rounded-lg bg-sky-50 border border-sky-200 px-3 py-1.5">
        <div className="text-sm font-heading font-semibold text-sky-900 tabular-nums leading-tight flex items-center gap-1.5" data-testid="med-safety-next-countdown">
          <Clock className="w-3.5 h-3.5 text-sky-700" aria-hidden="true" />
          {formatCountdown(nextDose.live_minutes_until)}
        </div>
        <div className="text-[11px] text-sky-800/80">at {nextDose.scheduled_time}</div>
      </div>
    </div>
  );
}

// ---- Empty state (all clear) ---------------------------------------------
function EmptyState({ summary, refreshedAt }) {
  return (
    <section
      className="rounded-xl border-2 shadow-sm overflow-hidden"
      style={{ backgroundColor: "#F0FDF4", borderColor: "#16A34A" }}
      role="status"
      aria-live="polite"
      data-testid="med-safety-empty"
    >
      <TopAccent tone="green" />
      <div className="p-4 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 border border-emerald-400 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-emerald-700" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-emerald-700">Medication Safety</div>
            <div className="text-sm font-heading font-semibold text-emerald-900">All scheduled medications are up to date</div>
            <div className="text-[13px] text-emerald-800/80">No overdue doses.</div>
            <div className="mt-1"><LastUpdated at={refreshedAt} testid="med-safety-updated-empty" /></div>
          </div>
        </div>
        <aside className="border-t lg:border-t-0 lg:border-l border-emerald-200 bg-white/60 rounded-md lg:rounded-none p-3 lg:pl-4">
          <TodayActivityPanel summary={summary} />
        </aside>
      </div>
    </section>
  );
}

// ---- Main -----------------------------------------------------------------
/**
 * MedicationSafetyAlert — enterprise safety card for the Executive Dashboard.
 * Refinement pass: red top-accent bar, dynamic Today's Activity swap when no
 * upcoming meds, contextual icons, 8-second gentle pulse, live "last updated"
 * timestamp, and a two-line overdue badge.
 */
export default function MedicationSafetyAlert({
  missedDoses = [],
  nextDoses = [],
  todaySummary = null,
}) {
  const [tick, setTick] = useState(0);
  const [refreshedAt] = useState(() => new Date());

  // Live-refresh timers every 60s without hitting the backend
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const overdue = useMemo(() => {
    return missedDoses.map((m) => {
      let minutes = m.overdue_minutes || 0;
      if (m.scheduled_at) {
        const diff = Math.floor((Date.now() - new Date(m.scheduled_at).getTime()) / 60_000);
        if (diff >= 0) minutes = diff;
      }
      return { ...m, live_overdue_minutes: minutes, _priority: priorityFor(minutes) };
    });
  }, [missedDoses, tick]);

  const nextDose = useMemo(() => {
    if (!nextDoses.length) return null;
    const n = nextDoses[0];
    let minutes = n.minutes_until || 0;
    if (n.scheduled_at) {
      const diff = Math.floor((new Date(n.scheduled_at).getTime() - Date.now()) / 60_000);
      if (!Number.isNaN(diff)) minutes = diff;
    }
    return { ...n, live_minutes_until: minutes };
  }, [nextDoses, tick]);

  const dueSoonCount = useMemo(
    () => (nextDoses || []).filter((n) => (n.minutes_until ?? 999) <= 30).length,
    [nextDoses],
  );

  if (!overdue.length) return <EmptyState summary={todaySummary} refreshedAt={refreshedAt} />;

  return (
    <section
      role="alert"
      aria-live="polite"
      aria-atomic="true"
      data-testid="med-safety-alert"
      className="rounded-xl border shadow-md overflow-hidden"
      style={{ backgroundColor: "#FEF2F2", borderColor: "#DC2626" }}
    >
      <style>{`
        @keyframes msaPulseGlow {
          0%, 82%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(220,38,38,0.0); }
          88% { transform: scale(1.04); box-shadow: 0 0 0 4px rgba(220,38,38,0.18); }
          94% { transform: scale(1.02); box-shadow: 0 0 0 8px rgba(220,38,38,0.0); }
        }
        .msa-pulse { animation: msaPulseGlow 8s ease-in-out infinite; }
        @keyframes msaFade {
          from { opacity: 0.6; transform: translateY(-1px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .msa-fade { animation: msaFade 240ms ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .msa-pulse, .msa-fade { animation: none; }
        }
      `}</style>

      {/* 1) Top red accent bar */}
      <TopAccent tone="red" />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-0">
        {/* Left column — header + overdue list + actions */}
        <div className="p-4 lg:px-5 lg:py-4">
          {/* Header */}
          <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <div
                className="w-10 h-10 rounded-full bg-red-100 border border-red-300 flex items-center justify-center shrink-0 msa-pulse"
                aria-hidden="true"
              >
                <AlertTriangle className="w-5 h-5 text-red-700" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <h2 className="font-heading font-semibold text-slate-900 text-base sm:text-lg leading-tight" data-testid="med-safety-title">
                  Medication Safety
                </h2>
                <div className="text-[13px] text-red-900/80 leading-snug" data-testid="med-safety-subtitle">
                  <span className="font-semibold text-red-800">{overdue.length}</span> overdue dose{overdue.length === 1 ? "" : "s"} require immediate attention.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-600 text-white text-sm font-semibold shadow-sm"
                data-testid="med-safety-overdue-badge"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" aria-hidden="true" />
                {overdue.length} Overdue
              </span>
              {dueSoonCount > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-sm font-semibold border border-amber-300"
                  data-testid="med-safety-duesoon-badge"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                  {dueSoonCount} Due Soon
                </span>
              )}
            </div>
          </header>

          {/* Overdue list */}
          <ul className="mt-3 divide-y divide-red-100/80 border-t border-red-100" data-testid="med-safety-list">
            {overdue.slice(0, 3).map((m) => {
              const person = {
                full_name: m.client_name,
                photo_url: m.client_photo_url,
                photo_tile_url: m.client_photo_tile_url,
                photo_thumb_url: m.client_photo_thumb_url,
              };
              return (
                <li
                  key={`${m.client_id}-${m.medication_id}-${m.scheduled_time}`}
                  className="py-2.5 flex items-center gap-3"
                  data-testid={`med-safety-row-${m.client_id}-${m.scheduled_time}`}
                >
                  {/* 36px avatar */}
                  <div style={{ width: 36, height: 36 }} className="shrink-0">
                    <PersonAvatar
                      person={person}
                      size="md"
                      className="!w-9 !h-9"
                      testid={`med-safety-avatar-${m.client_id}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate leading-tight flex items-center gap-1">
                      <User className="w-3.5 h-3.5 text-slate-500 shrink-0" aria-hidden="true" />
                      {m.client_name}
                    </div>
                    <div className="text-[13px] text-slate-700 mt-0.5 flex items-center gap-1.5 flex-wrap leading-snug">
                      <Pill className="w-3.5 h-3.5 text-red-700 shrink-0" aria-hidden="true" />
                      <span className="font-semibold text-slate-900">{m.medication_name}</span>
                      <span className="text-slate-400">·</span>
                      <Clock className="w-3.5 h-3.5 text-slate-500 shrink-0" aria-hidden="true" />
                      <span className="tabular-nums text-slate-600">{scheduleLabel(m.scheduled_at, m.scheduled_time)}</span>
                      {m.is_controlled_drug && (
                        <span className="uppercase tracking-widest text-[9px] font-bold text-red-900 border border-red-400 px-1 rounded ml-0.5">CD</span>
                      )}
                    </div>
                  </div>
                  {/* 6) Two-line overdue badge */}
                  <span
                    key={m.live_overdue_minutes}
                    className={`msa-fade inline-flex flex-col items-center justify-center leading-none px-2.5 py-1 rounded-lg border shrink-0 min-w-[64px] ${m._priority.chip}`}
                    data-testid={`med-safety-timer-${m.client_id}`}
                    aria-label={`${formatOverdue(m.live_overdue_minutes)} overdue, priority ${m._priority.label}`}
                    title={`${m._priority.label} · ${formatOverdue(m.live_overdue_minutes)} overdue`}
                  >
                    <span className="flex items-center gap-1 text-[13px] font-heading font-bold tabular-nums">
                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-90" aria-hidden="true" />
                      {formatOverdue(m.live_overdue_minutes)}
                    </span>
                    <span className="mt-0.5 text-[9px] font-bold tracking-[0.14em] uppercase opacity-95">
                      Overdue
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>

          {overdue.length > 3 && (
            <Link
              to="/mar-chart"
              className="inline-flex items-center gap-1 mt-1 text-[13px] font-medium text-red-800 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-400 rounded"
              data-testid="med-safety-more"
            >
              +{overdue.length - 3} more overdue medication{overdue.length - 3 === 1 ? "" : "s"}
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          )}

          {/* Actions — 44px min height */}
          <div className="mt-3.5 flex flex-col sm:flex-row gap-2">
            <Button
              asChild
              className="h-11 bg-sky-700 hover:bg-sky-800 text-white font-semibold shadow-sm w-full sm:w-auto"
              data-testid="med-safety-open-mar"
            >
              <Link to="/mar-chart" className="inline-flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Open MAR Dashboard
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="h-11 bg-white border-slate-300 text-slate-800 hover:bg-slate-50 hover:border-slate-400 w-full sm:w-auto"
              data-testid="med-safety-view-all"
            >
              <Link to="/mar-chart" className="inline-flex items-center gap-2">
                View All Alerts
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>

          {/* 5) Last updated */}
          <div className="mt-2">
            <LastUpdated at={refreshedAt} />
          </div>
        </div>

        {/* Right column — dynamic panel */}
        <aside
          className="border-t lg:border-t-0 lg:border-l border-red-200 bg-white/70 p-4 lg:px-5 lg:py-4 flex flex-col justify-center"
          aria-label={nextDose ? "Next medication due" : "Today's medication activity"}
        >
          {nextDose ? (
            <NextDosePanel nextDose={nextDose} />
          ) : (
            <TodayActivityPanel summary={todaySummary} />
          )}
        </aside>
      </div>
    </section>
  );
}
